import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FORM_ACTION = "https://readdy.ai/api/form/db88j49rh5tg6socjrhg";

const contentScope = [
  { icon: "ri-movie-2-line", title: "动画资源索引", text: "按分类、年份、评分整理日本动画作品信息，提供剧情简介与作品标签，方便快速检索。" },
  { icon: "ri-user-heart-line", title: "角色资料整理", text: "为作品建立主要角色档案，包含性格、故事线与人物关系，帮助理解剧情脉络。" },
  { icon: "ri-newspaper-line", title: "行业资讯报道", text: "汇总定档速报、制作访谈与行业观察，持续跟进日本动画圈的最新动态。" },
];

const commitments = [
  "站内所有动画、角色与资讯内容均为演示用途，不涉及真实版权作品信息。",
  "本站不提供任何在线播放、下载或资源分发服务，仅用于作品介绍与资料展示。",
  "用户在使用本站过程中产生的收藏等数据仅保存在本地浏览器，不会上传服务器。",
];

export default function AboutPage() {
  useDocumentMeta({
    title: "日漫 - 关于我们，日本动画资源与资讯平台",
    description:
      "了解日漫网站的定位与内容范围：专注日本动画资源索引、角色资料整理与行业资讯报道，并提供意见反馈入口与隐私说明。",
    keywords: "关于日漫,动漫网站,日本动画平台,动漫资讯,联系方式",
  });

  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const guard = String(formData.get("website_alt") || "").trim();
    if (guard) {
      setFormStatus("success");
      setFormMessage("提交成功，感谢你的反馈，我们会尽快处理！");
      formEl.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setFormStatus("submitting");
    setFormMessage("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMessage);
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setFormStatus("success");
        setFormMessage("提交成功，感谢你的反馈，我们会尽快处理！");
        formEl.reset();
      } else {
        setFormStatus("error");
        setFormMessage(serverMessage || "提交失败，请稍后重试，或直接通过邮箱联系我们。");
      }
    } catch {
      setFormStatus("error");
      setFormMessage("网络异常，提交失败，请稍后重试，或直接通过邮箱联系我们。");
    }
  };

  return (
    <Layout>
      <div className="w-full px-4 md:px-6 pt-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      </div>

      <section className="w-full px-4 md:px-6 pt-6 pb-4">
        <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-6 md:p-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-label font-semibold uppercase tracking-wider text-primary-600">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            About Riman
          </span>
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
            关于日漫
          </h1>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600 max-w-3xl">
            「日漫」是一个专注日本动画资源与行业资讯的门户网站，面向喜爱日本动画的中青年动漫爱好者。我们把零散的作品信息整理成清晰可检索的结构，帮助你在开始观看之前，就能了解剧情走向、角色设置与作品标签，也持续跟踪动画行业的最新动态。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">内容范围</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
          {contentScope.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="w-11 h-11 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                <i className={`${item.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如果你对内容有建议、发现信息有误，或希望与我们进行内容合作，欢迎通过下方表单留言，也可以直接发送邮件。
            </p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="font-medium text-foreground-950">邮箱反馈</p>
                  <p className="text-foreground-600">contact@riman-anime.demo</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                  <i className="ri-customer-service-2-line" />
                </span>
                <div>
                  <p className="font-medium text-foreground-950">客服热线</p>
                  <p className="text-foreground-600">+86 400-000-0000（工作日 10:00 - 18:00）</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line" />
                </span>
                <div>
                  <p className="font-medium text-foreground-950">办公地址</p>
                  <p className="text-foreground-600">上海市浦东新区动漫创意园区 8 号楼</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              id="riman-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <h3 className="font-heading text-lg font-bold text-foreground-950">意见反馈表单</h3>
              <p className="mt-1 text-xs text-foreground-500">带 * 的字段为必填项，我们会在 2 个工作日内回复。</p>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    称呼 *
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full h-11 rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱 *
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full h-11 rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  反馈类型
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  defaultValue="内容建议"
                  className="w-full h-11 rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors cursor-pointer"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="信息纠错">信息纠错</option>
                  <option value="内容合作">内容合作</option>
                  <option value="其他问题">其他问题</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  留言内容 *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请简要描述你的建议或问题（最多 500 字）"
                  className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-300 transition-colors resize-none"
                />
              </div>

              <div className="form-guard-slot" aria-hidden="true">
                <label htmlFor="contact-website-alt">网站主页（请勿填写）</label>
                <input
                  id="contact-website-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>
                <span className="text-xs text-foreground-500">本表单仅用于收集反馈，不会用于其他用途。</span>
              </div>

              {formStatus === "success" && (
                <p className="mt-4 flex items-start gap-2 text-sm text-secondary-700 bg-secondary-50 border border-secondary-200 rounded-md px-3 py-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5" />
                  {formMessage}
                </p>
              )}
              {formStatus === "error" && (
                <p className="mt-4 flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5">
                  <i className="ri-error-warning-line mt-0.5" />
                  {formMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">法律声明与隐私政策</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 max-w-4xl">
            本站为演示性质的内容平台，站内展示的动画作品、角色与资讯均为虚构示例，用于呈现产品的信息组织与交互方式。我们尊重知识产权，本平台不提供任何受版权保护内容的播放、下载或分发服务。
          </p>
          <ul className="mt-5 space-y-3">
            {commitments.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                <i className="ri-shield-check-line text-primary-500 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm leading-relaxed text-foreground-600 max-w-4xl">
            关于隐私：我们仅在你主动提交反馈表单时收集必要信息（称呼、邮箱与留言内容），用于回复你的提问，不会向第三方出售或共享。你可以随时通过上方邮箱要求删除已提交的信息。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-compass-3-line" />
              浏览动画分类
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-newspaper-line" />
              查看动漫资讯
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}